import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2 } from "lucide-react";
import api, { currency, formatApiError } from "@/lib/api";

const STATUS_COLORS = {
  Draft: "text-zinc-300 bg-zinc-700/40",
  Synced: "text-blue-400 bg-blue-500/15",
  Paid: "text-green-400 bg-green-500/15",
};

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const load = () => api.get("/invoices").then(({ data }) => setInvoices(data));
  useEffect(load, []);

  const sync = async (inv) => {
    try {
      await api.post(`/invoices/${inv.id}/sync-quickbooks`);
      toast.success(`${inv.invoice_number} synced to QuickBooks`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const pay = async (inv) => {
    try {
      await api.post(`/invoices/${inv.id}/mark-paid`);
      toast.success(`${inv.invoice_number} marked paid`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-display text-3xl font-black uppercase tracking-tighter">Invoices</h1>
        <p className="text-zinc-500 text-sm">One-click QuickBooks sync & mark-paid <span className="text-yellow-600">(QuickBooks sync is a demo mock)</span></p>
      </div>

      <div className="border border-zinc-800 bg-zinc-950">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-zinc-500 uppercase text-xs">
              <th className="px-4 py-3">Invoice</th><th>Build</th><th>Customer</th><th>QuickBooks</th><th className="text-right">Amount</th><th>Status</th><th className="text-right px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.id} className="border-t border-zinc-800 even:bg-zinc-900/40" data-testid={`invoice-row-${inv.invoice_number}`}>
                <td className="px-4 py-3 font-semibold">{inv.invoice_number}</td>
                <td className="text-zinc-400">{inv.build_number}</td>
                <td>{inv.customer_name}</td>
                <td className="text-zinc-400">{inv.quickbooks_id || "—"}</td>
                <td className="text-right font-semibold">{currency(inv.amount)}</td>
                <td><span className={`text-xs font-bold uppercase px-2 py-1 ${STATUS_COLORS[inv.status]}`}>{inv.status}</span></td>
                <td className="text-right px-4 py-3 space-x-2 whitespace-nowrap">
                  {inv.status === "Draft" && (
                    <button onClick={() => sync(inv)} data-testid={`sync-${inv.invoice_number}`}
                      className="text-xs font-bold uppercase border border-zinc-700 px-3 py-1.5 hover:border-yellow-500 hover:text-yellow-500 transition-colors duration-150 inline-flex items-center gap-1">
                      <RefreshCw size={13} /> Sync QB
                    </button>
                  )}
                  {inv.status !== "Paid" && (
                    <button onClick={() => pay(inv)} data-testid={`pay-${inv.invoice_number}`}
                      className="text-xs font-bold uppercase bg-yellow-500 text-black px-3 py-1.5 hover:bg-yellow-300 transition-colors duration-150 inline-flex items-center gap-1">
                      <CheckCircle2 size={13} /> Mark Paid
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {invoices.length === 0 && (
              <tr><td colSpan={7} className="text-center text-zinc-600 py-10">No invoices yet. Create one from a build.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
